import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ButtonRadioGroup } from '../../components/radioButton/RadioButton';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, TextField, Typography } from '@mui/material';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { calculateScore, getRecommendationForScore, getRecommendationSummary, getScoreDisplayValue, getScorePosition, KAISER_URL } from './utilsV2';
import GoToNewVersionButton from './GoToNewVersionButton';
import { useClipboard } from '../../hooks/useClipboard';
import { saveCardiologyData } from '../../api/patient.api';
import { useAuth } from '../../context/auth-context';
import './Cardiology.scss';

const CardiologyV2 = () => {
    const navigate = useNavigate();
    const { userInfo } = useAuth();
    const { copyToClipboard } = useClipboard();
    const [ isSnackbarOpen, setIsSnackbarOpen ] = useState<boolean>(false);
    const [ loading, setLoading ] = useState<boolean>(false);
    const [ isSaveLoading, setIsSaveLoading ] = useState<boolean>(false);
    const [ hasKnowsCAD, setHasKnowsCAD ] = useState<boolean>(false);
    const [ patientEmail, setPatientEmail ] = useState<string>('');
    const [ isModalOpen, setIsModalOpen ] = useState<boolean>(false);
    const [ familyHistory ] = useState<boolean>(false);
    // const [ painLevel ] = useState<number>(0);
    const [ age, setAge ] = useState<number>();
    const [ sex, setSex ] = useState<string>('male');
    const [ chestPain, setChestPain ] = useState<'Cardiac' | 'Possibly Cardiac' | 'Non-Cardiac'>('Non-Cardiac');
    const [ diabetes, setDiabetes ] = useState<boolean>(false);
    const [ hypertension, setHypertension ] = useState<boolean>(false);
    const [ dyslipidemia, setDyslipidemia ] = useState<boolean>(false);
    const [ smoker, setSmoker ] = useState<boolean>(false);
    const [ coronaryCalciumScoreAvailable, setCoronaryCalciumScoreAvailable ] = useState<boolean>(false);
    const [ coronaryCalciumScore, setCoronaryCalciumScore ] = useState<number>();
    const [ score, setScore ] = useState<number>(0);
    const [ canExercise, setCanExercise ] = useState<'yes' | 'no'>();
    const [ wasECGWithin6Months, setWasECGWithin6Months ] = useState<'yes' | 'no'>();
    const [ wasECGInterpretable, setWasECGInterpretable ] = useState<'yes' | 'no'>();
    const [ ctaExpertiseAvailable, setCtaExpertiseAvailable ] = useState<'yes' | 'no'>();
    const [ recommendation, setRecommendation ] = useState<string>('');

    useEffect(() => {
        setIsModalOpen(true);
    }, []);

    useEffect(() => {
        setScore(
            calculateScore(
                age || 0,
                sex as 'male' | 'female',
                chestPain,
                diabetes,
                hypertension,
                dyslipidemia,
                smoker,
                coronaryCalciumScoreAvailable,
                coronaryCalciumScore || 0,
            ),
        );
    }, [ age, sex, chestPain, diabetes, hypertension, dyslipidemia, smoker, coronaryCalciumScoreAvailable, coronaryCalciumScore ]);

    useEffect(() => {
        setRecommendation(
            getRecommendationForScore(
                score,
                canExercise,
                wasECGWithin6Months,
                wasECGInterpretable,
                ctaExpertiseAvailable,
                age,
            ),
        );
    }, [ score, canExercise, wasECGWithin6Months, wasECGInterpretable, ctaExpertiseAvailable, age ]);

    const summary = getRecommendationSummary(
        age || 0,
        sex as 'male' | 'female',
        chestPain as 'cardiac' | 'possibly-cardiac' | 'non-cardiac',
        diabetes,
        hypertension,
        dyslipidemia,
        smoker,
        coronaryCalciumScoreAvailable,
        coronaryCalciumScore || 0,
        score,
        recommendation,
    );

    const handleClose = (_?: {}, reason?: 'backdropClick' | 'escapeKeyDown') => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
        }

        setIsModalOpen(false);
    };

    const onConfirm = () => {
        if (hasKnowsCAD) {
            setLoading(true);
            return window.location.href = KAISER_URL;
        }

        setIsModalOpen(false);
    };

    const onCopyToClipboard = async (text: string) => {
        const copied = await copyToClipboard(text);
        if (copied) {
            setIsSnackbarOpen(true);
        }
    };

    const onSaveToDashboard = async () => {
        setIsSaveLoading(true);
        saveCardiologyData({
            id: +(
                userInfo?.id || 123
            ),
            email: patientEmail,
            aiScore: 1,
            score,
            patientEpicId: 1,
            data: {
                age: age || 0,
                chestPain,
                sex: sex === 'male' ? 'M' : 'F',
                family: familyHistory ? 'Y' : 'N',
                diabetes: diabetes ? 'Y' : 'N',
                hypertension: hypertension ? 'Y' : 'N',
                dyslipidemia: dyslipidemia ? 'Y' : 'N',
                smoker: smoker ? 'Y' : 'N',
                coronaryCalciumScoreAvailable: coronaryCalciumScore || 0,
            },
        })
        .finally(() => setIsSaveLoading(false));
    };

    return (
        <div className="cardiology">
            <div className="cardiology-container">
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        position: 'absolute',
                        right: 32,
                        top: 37,
                    }}
                >
                    <GoToNewVersionButton
                        onClick={() => navigate('/cardiology-new')}
                    >
                        See new version
                    </GoToNewVersionButton>
                </div>
                <div className="cardiology_patientDataSection">
                    <div className="cardiology_patientDataSection_header">
                        Enter Patient Data
                    </div>
                    <div className="cardiology_patientDataSection_form">
                        <div className="cardiology_patientDataSection_form_fieldSection">
                            <div className="cardiology_patientDataSection_form_fieldSection_name">
                                Age
                            </div>
                            <div className="cardiology_patientDataSection_form_fieldSection_content">
                                <TextField
                                    label="Age"
                                    size="small"
                                    type="number"
                                    value={age}
                                    sx={{ width: 300 }}
                                    onChange={(e) => setAge(+e.target.value)}
                                    InputProps={{ inputProps: { min: 1, max: 120, step: 1 } }}
                                />
                            </div>
                        </div>
                        <div className="cardiology_patientDataSection_form_fieldSection">
                            <div className="cardiology_patientDataSection_form_fieldSection_name">
                                Sex
                            </div>
                            <div className="cardiology_patientDataSection_form_fieldSection_content">
                                <ButtonRadioGroup
                                    value={sex}
                                    onChange={value => setSex(value)}
                                    options={[
                                        { value: 'male', label: 'Male' },
                                        { value: 'female', label: 'Female' },
                                    ]}
                                />
                            </div>
                        </div>
                        <div className="cardiology_patientDataSection_form_fieldSection">
                            <div className="cardiology_patientDataSection_form_fieldSection_name">
                                Chest pain
                            </div>
                            <div className="cardiology_patientDataSection_form_fieldSection_content">
                                <ButtonRadioGroup
                                    value={chestPain}
                                    onChange={value => setChestPain(value)}
                                    options={[
                                        {
                                            value: 'Cardiac',
                                            label: 'Cardiac',
                                            details: (
                                                <ul style={{ paddingLeft: 30 }}>
                                                    <li>Central</li>
                                                    <li>Pressure</li>
                                                    <li>Squeezing</li>
                                                    <li>Gripping</li>
                                                    <li>Heaviness</li>
                                                    <li>Tightness</li>
                                                    <li>Exertional / stress-related</li>
                                                    <li>Retrosternal</li>
                                                </ul>
                                            ),
                                        },
                                        {
                                            value: 'Possibly Cardiac',
                                            label: 'Possibly Cardiac',
                                            details: (
                                                <ul style={{ paddingLeft: 30 }}>
                                                    <li>Left sided</li>
                                                    <li>Dull</li>
                                                    <li>Aching</li>
                                                    <li>Stabbing</li>
                                                    <li>Right sided</li>
                                                    <li>Tearing</li>
                                                    <li>Ripping</li>
                                                    <li>Burning</li>
                                                </ul>
                                            ),
                                        },
                                        {
                                            value: 'Non-Cardiac',
                                            label: 'Non-Cardiac',
                                            details: (
                                                <ul style={{ paddingLeft: 30 }}>
                                                    <li>Sharp</li>
                                                    <li>Fleeting</li>
                                                    <li>Shifting</li>
                                                    <li>Pleuritic</li>
                                                    <li>Positional</li>
                                                    <li>Right sided</li>
                                                </ul>
                                            ),
                                        },
                                    ]}
                                    optionClassName="radio-button-label"
                                    detailClassName="radio-button-detail"
                                />
                                <div className="cardiology-info">
                                    *if patient symptoms fall into different categories, clinical judgement is advised
                                </div>
                            </div>
                        </div>
                        <div className="cardiology_patientDataSection_form_fieldSection">
                            <div className="cardiology_patientDataSection_form_fieldSection_name">
                                Diabetes
                            </div>
                            <div className="cardiology_patientDataSection_form_fieldSection_content">
                                <ButtonRadioGroup<boolean>
                                    value={diabetes}
                                    onChange={value => setDiabetes(value)}
                                    options={[
                                        { value: true, label: 'Yes' },
                                        { value: false, label: 'No' },
                                    ]}
                                />
                            </div>
                        </div>
                        <div className="cardiology_patientDataSection_form_fieldSection">
                            <div className="cardiology_patientDataSection_form_fieldSection_name">
                                Hypertension
                            </div>
                            <div className="cardiology_patientDataSection_form_fieldSection_content">
                                <ButtonRadioGroup<boolean>
                                    value={hypertension}
                                    onChange={value => setHypertension(value)}
                                    options={[
                                        { value: true, label: 'Yes' },
                                        { value: false, label: 'No' },
                                    ]}
                                />
                            </div>
                        </div>
                        <div className="cardiology_patientDataSection_form_fieldSection">
                            <div className="cardiology_patientDataSection_form_fieldSection_name">
                                Dyslipidemia
                            </div>
                            <div className="cardiology_patientDataSection_form_fieldSection_content">
                                <ButtonRadioGroup<boolean>
                                    value={dyslipidemia}
                                    onChange={value => setDyslipidemia(value)}
                                    options={[
                                        { value: true, label: 'Yes' },
                                        { value: false, label: 'No' },
                                    ]}
                                />
                            </div>
                        </div>
                        <div className="cardiology_patientDataSection_form_fieldSection">
                            <div className="cardiology_patientDataSection_form_fieldSection_name">
                                Current or past smoker
                            </div>
                            <div className="cardiology_patientDataSection_form_fieldSection_content">
                                <ButtonRadioGroup<boolean>
                                    value={smoker}
                                    onChange={value => setSmoker(value)}
                                    options={[
                                        { value: true, label: 'Yes' },
                                        { value: false, label: 'No' },
                                    ]}
                                />
                            </div>
                        </div>
                        <div className="cardiology_patientDataSection_form_fieldSection">
                            <div className="cardiology_patientDataSection_form_fieldSection_name">
                                Coronary Calcium Score Available?
                            </div>
                            <div className="cardiology_patientDataSection_form_fieldSection_content">
                                <ButtonRadioGroup<boolean>
                                    value={coronaryCalciumScoreAvailable}
                                    onChange={value => setCoronaryCalciumScoreAvailable(value)}
                                    options={[
                                        { value: true, label: 'Yes' },
                                        { value: false, label: 'No' },
                                    ]}
                                />
                            </div>
                        </div>
                        <div className="cardiology_patientDataSection_form_fieldSection">
                            <div className="cardiology_patientDataSection_form_fieldSection_name">
                                Coronary Calcium Score
                            </div>
                            <div className="cardiology_patientDataSection_form_fieldSection_content">
                                <TextField
                                    label="Score"
                                    size="small"
                                    type="number"
                                    value={coronaryCalciumScore}
                                    onChange={(e) => setCoronaryCalciumScore(+e.target.value)}
                                    disabled={!coronaryCalciumScoreAvailable}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="cardiology_patientScoreSection">
                    <div className="cardiology_patientDataSection_header">
                        Calculated Results
                    </div>
                    <div className="cardiology_patientScoreSection_content">
                        <div className="cardiology_patientScoreSection_content_scoreTitle">
                            Probability of Coronary Artery Disease:
                        </div>
                        <div className="cardiology_patientScoreSection_content_scoreSection">
                            <div className="cardiology_patientScoreSection_content_scoreSection_left">
                                <div className="cardiology_patientScoreSection_content_scoreSection_left_content">
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <div>
                                            <div
                                                className="cardiology_patientScoreSection_content_scoreSection_left_content_score">
                                                {getScoreDisplayValue(score)}
                                            </div>
                                            <div
                                                className="cardiology_patientScoreSection_content_scoreSection_left_content_scoreDescription">
                                                Clinic + CCS Model
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        className="cardiology_patientScoreSection_content_scoreSection_left_content_scoreDescription2">
                                        <b>Intermediate</b> pre-test probability of coronary artery disease.
                                    </div>
                                </div>
                            </div>
                            <div className="cardiology_patientScoreSection_content_scoreSection_right">
                                <div className="score-rectangle">
                                    <div className="high">High</div>
                                    <div className="med">Med</div>
                                    <div className="low">Low</div>
                                    <div className="zero"/>
                                    <div
                                        className="yourRisk"
                                        style={{
                                            top: `calc(${(
                                                getScorePosition(score)
                                            )}% - 4px)`,
                                        }}
                                    >
                                        <div className="rect"/>
                                        ← Your Risk
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {(
                        score > 15 && score < 85
                    ) && (
                        <div className="cardiology_patientScoreSection_suggestedActions">
                            <div className="cardiology_patientScoreSection_suggestedActions_title">
                                Suggested Course of action
                            </div>
                            <div className="cardiology_patientDataSection_form_fieldSection">
                                <div className="cardiology_patientDataSection_form_fieldSection_name">
                                    Can the patient exercise safely on a treadmill
                                </div>
                                <div className="cardiology_patientDataSection_form_fieldSection_content">
                                    <ButtonRadioGroup<'yes' | 'no'>
                                        emptyDefaultValue
                                        value={canExercise}
                                        onChange={value => setCanExercise(value)}
                                        options={[
                                            { value: 'yes', label: 'Yes' },
                                            { value: 'no', label: 'No' },
                                        ]}
                                    />
                                </div>
                            </div>
                            {canExercise === 'yes' ? (
                                <>
                                    <div className="cardiology_patientDataSection_form_fieldSection">
                                        <div className="cardiology_patientDataSection_form_fieldSection_name">
                                            Was ECG done within 6 months?
                                        </div>
                                        <div className="cardiology_patientDataSection_form_fieldSection_content">
                                            <ButtonRadioGroup<'yes' | 'no'>
                                                emptyDefaultValue
                                                value={wasECGWithin6Months}
                                                onChange={value => setWasECGWithin6Months(value)}
                                                options={[
                                                    { value: 'yes', label: 'Yes' },
                                                    { value: 'no', label: 'No' },
                                                ]}
                                            />
                                        </div>
                                    </div>
                                    {wasECGWithin6Months === 'yes' && (
                                        <div className="cardiology_patientDataSection_form_fieldSection">
                                            <div className="cardiology_patientDataSection_form_fieldSection_name">
                                                Is the ECG interpretable?
                                            </div>
                                            <div className="cardiology_patientDataSection_form_fieldSection_content">
                                                <ButtonRadioGroup<'yes' | 'no'>
                                                    emptyDefaultValue
                                                    value={wasECGInterpretable}
                                                    onChange={value => setWasECGInterpretable(value)}
                                                    options={[
                                                        { value: 'yes', label: 'Yes' },
                                                        { value: 'no', label: 'No' },
                                                    ]}
                                                />
                                            </div>
                                        </div>
                                    )}
                                    {
                                        wasECGWithin6Months === 'yes'
                                        && wasECGInterpretable === 'no'
                                        && (
                                            age || 0
                                        ) >= 70
                                        && (
                                            <div className="cardiology_patientDataSection_form_fieldSection">
                                                <div className="cardiology_patientDataSection_form_fieldSection_name">
                                                    CTA expertise / technology available AND suitable candidate for CTA?
                                                </div>
                                                <div className="cardiology_patientDataSection_form_fieldSection_content">
                                                    <ButtonRadioGroup<'yes' | 'no'>
                                                        emptyDefaultValue
                                                        value={ctaExpertiseAvailable}
                                                        onChange={value => setCtaExpertiseAvailable(value)}
                                                        options={[
                                                            { value: 'yes', label: 'Yes' },
                                                            { value: 'no', label: 'No' },
                                                        ]}
                                                    />
                                                </div>
                                            </div>
                                        )
                                    }
                                </>
                            ) : canExercise === 'no' && (
                                <div className="cardiology_patientDataSection_form_fieldSection">
                                    <div className="cardiology_patientDataSection_form_fieldSection_name">
                                        CTA expertise / technology available AND suitable candidate for CTA?
                                    </div>
                                    <div className="cardiology_patientDataSection_form_fieldSection_content">
                                        <ButtonRadioGroup<'yes' | 'no'>
                                            emptyDefaultValue
                                            value={ctaExpertiseAvailable}
                                            onChange={value => setCtaExpertiseAvailable(value)}
                                            options={[
                                                { value: 'yes', label: 'Yes' },
                                                { value: 'no', label: 'No' },
                                            ]}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    {recommendation && (
                        <>
                            <div className="cardiology_patientScoreSection_cta">
                                Consider {recommendation}.
                            </div>
                            <div className="cardiology_patientScoreSection_ctaContent">
                                <div className="cardiology_patientScoreSection_ctaContent_title">
                                    Summary
                                </div>
                                <div
                                    className="cardiology_patientScoreSection_ctaContent_summary"
                                    title="Click to copy."
                                    onClick={() => onCopyToClipboard(summary)}
                                >
                                    {summary}
                                </div>
                                <Snackbar
                                    open={isSnackbarOpen}
                                    onClose={() => setIsSnackbarOpen(false)}
                                    autoHideDuration={1500}
                                    message="Copied!"
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                />
                            </div>
                        </>
                    )}
                </div>
                <Dialog open={isModalOpen} onClose={handleClose}>
                    <DialogTitle
                        sx={{
                            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                            paddingX: 2,
                            paddingY: 1,
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <ReportProblemIcon color="error" fontSize="medium"/>
                            <Typography
                                variant="h6"
                                color="error"
                                fontWeight="bold"
                                sx={{ ml: 1 }}
                            >
                                STOP
                            </Typography>
                        </div>
                    </DialogTitle>
                    <DialogContent
                        sx={{
                            minWidth: 300,
                            maxWidth: 400,
                            paddingX: 2,
                            paddingY: 1,
                            paddingTop: '8px !important',
                            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <div className="cardiology_modal_title">
                            This tool should only be used for patients WITHOUT known CAD.
                        </div>
                        <div className="cardiology_modal_subtitle">
                            For patients with a history of stents, CABG or suspected ACS (acute coronary syndrome),
                            please
                            consider discussing with a cardiologist about options for risk stratification.
                        </div>
                        <div
                            className="cardiology_patientDataSection_form_fieldSection"
                            style={{ display: 'flex', justifyContent: 'space-between' }}
                        >
                            <div className="cardiology_patientDataSection_form_fieldSection_name">
                                Do you have knows CAD?
                            </div>
                            <div className="cardiology_patientDataSection_form_fieldSection_content">
                                <ButtonRadioGroup<boolean>
                                    value={hasKnowsCAD}
                                    onChange={value => setHasKnowsCAD(value)}
                                    options={[
                                        { value: true, label: 'Yes' },
                                        { value: false, label: 'No' },
                                    ]}
                                />
                            </div>
                        </div>
                        <div
                            className="cardiology_patientDataSection_form_fieldSection"
                            style={{ display: 'flex', justifyContent: 'space-between' }}
                        >
                            <div className="cardiology_patientDataSection_form_fieldSection_name">
                                Patient email
                            </div>
                            <div className="cardiology_patientDataSection_form_fieldSection_content">
                                <TextField
                                    label="Patient email"
                                    size="small"
                                    type="email"
                                    value={patientEmail}
                                    sx={{ width: 200 }}
                                    onChange={(e) => setPatientEmail(e.target.value)}
                                    disabled={hasKnowsCAD}
                                />
                            </div>
                        </div>
                    </DialogContent>
                    <DialogActions sx={{ padding: 2 }}>
                        <Button
                            onClick={onConfirm}
                            color="primary"
                            variant="contained"
                            disabled={(
                                !hasKnowsCAD && patientEmail.length === 0
                            ) || loading}
                            startIcon={loading ? <CircularProgress size={16}/> : undefined}
                        >
                            Confirm
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>
            <div className="cardiology-cta">
                <Button
                    startIcon={isSaveLoading ? <CircularProgress size={16}/> : undefined}
                    onClick={onSaveToDashboard}
                    color="primary"
                    variant="contained"
                    disabled={isSaveLoading || !recommendation}
                    style={{ cursor: 'pointer' }}
                >
                    Save to Dashboard or Epic
                </Button>
                {/*<Snackbar*/}
                {/*    open={isSnackbarOpen}*/}
                {/*    onClose={() => setIsSnackbarOpen(false)}*/}
                {/*    autoHideDuration={1500}*/}
                {/*    message="Patient data is saved!"*/}
                {/*    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}*/}
                {/*/>*/}
            </div>
        </div>
    );
};

export default CardiologyV2;
